import { types } from "@gadgetinc/mobx-quick-tree";
import { parse } from "chrono-node";
import { isString } from "lodash";
import { DateTime } from "luxon";

export const LuxonDate = types.custom<string, DateTime>({
  name: "LuxonDate",

  fromSnapshot(snapshot, _env) {
    return DateTime.fromISO(snapshot);
  },
  toSnapshot(value) {
    return value.toISO()!;
  },
  isTargetType(value) {
    return !isString(value);
  },
  getValidationMessage(snapshot) {
    try {
      const value = DateTime.fromISO(snapshot);
      return value.invalidExplanation ?? "";
    } catch (e) {
      return String(e);
    }
  },
});

export const FixedTimeRange = types
  .model("FixedTimeRange", {
    start: types.optional(LuxonDate, () => DateTime.local().plus({ hours: -1 })),
    end: types.optional(LuxonDate, () => DateTime.local()),
  })
  .views((self) => ({
    get autoTitle() {
      return `${self.start.toLocaleString(DateTime.DATETIME_SHORT)} - ${self.end.toLocaleString(DateTime.DATETIME_SHORT)}`;
    },
    get title() {
      return this.autoTitle;
    },
  }))
  .actions((self) => ({
    setStart(dt: DateTime) {
      if (dt > self.end) {
        throw new Error("Can't set the start of a range to after the end of a range");
      }
      self.start = dt;
    },
    setEnd(dt: DateTime) {
      if (dt < self.start) {
        throw new Error("Can't set the end of a range to before the start of a range");
      }
      self.end = dt;
    },
    setRange(start: DateTime, end: DateTime) {
      if (start > end) {
        throw new Error("Can't set the start of a range to after the end of a range");
      }
      self.start = start;
      self.end = end;
    },
  }));

export const DynamicTimeRange = types
  .model("DynamicTimeRange", {
    input: types.optional(types.string, "Last 5 mins"),
  })
  .volatile(() => ({
    anchor: DateTime.local(),
  }))
  .views((self) => ({
    get parseResult() {
      return parse(self.input, self.anchor.toJSDate())[0];
    },
  }))
  .views((self) => ({
    get valid() {
      return !!self.parseResult;
    },
    get start() {
      if (self.parseResult) {
        return DateTime.fromJSDate(self.parseResult.start.date());
      } else {
        return DateTime.local().plus({ hours: -1 });
      }
    },
    get end() {
      if (self.parseResult && self.parseResult.end) {
        return DateTime.fromJSDate(self.parseResult.end.date());
      }

      return DateTime.local();
    },
    get title() {
      return self.input;
    },
  }))
  .actions((self) => ({
    // Recompute everything again, anchored to the current time instead of the initial anchor time
    update() {
      self.anchor = DateTime.local();
    },
    setInput(input: string) {
      self.input = input;
    },
  }));
